import { TFunction } from 'i18next';
import { Cable, Quote, QuoteProduct } from '../../models';
import {
  getDefaultQuoteProduct,
  updateQuoteProductFromCable,
} from '../../utils/quoteProduct';
import { convertQuoteProductQuantity } from '../../utils/quote';
import { MeasurementUnits } from '../../enums';

export class QuoteImportCsv {
  private readonly data: string;
  private readonly t: TFunction<'translation', undefined>;
  private readonly cables: Cable[] = [];
  private readonly units: MeasurementUnits;
  private readonly quote: Quote | null;
  protected products: QuoteProduct[] = [];
  protected errors: Error[] = [];

  constructor(
    data: string,
    t: TFunction,
    cables: Cable[],
    units: MeasurementUnits,
    quote: Quote | null
  ) {
    this.data = data;
    this.t = t;
    this.cables = cables;
    this.units = units;
    this.quote = quote;
  }

  protected extractNumberFromString(input: string): number {
    const match = input.match(/^\d+\.*\d+/);
    return match ? Number(match[0]) : NaN;
  }

  protected extractMeasurementUnitFromString(input: string): string {
    const match = input.match(/([a-z]+$)/);
    const unit = match ? match[0] : '';

    if (unit === 'ft') {
      return 'imperial';
    } else if (unit === 'm') {
      return 'metric';
    } else {
      return unit;
    }
  }

  protected convertQuantity(quantity: number, unit: string): number {
    return unit && unit !== this.units
      ? convertQuoteProductQuantity(quantity, this.units)
      : quantity;
  }

  protected parse() {
    const lines = this.data.split('\r\n').filter((line) => line.trim() !== '');

    if (lines.length < 2) {
      this.errors.push(new Error(this.t('import.noDataError')));
      return;
    }

    let count = this.quote?.products?.length || 0;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const [partNumber, quantity, unitPrice] = line
        .split(',')
        .map((value) => value.trim());

      if (
        !partNumber ||
        isNaN(this.extractNumberFromString(quantity)) ||
        isNaN(this.extractNumberFromString(unitPrice))
      ) {
        this.errors.push(new Error(this.t('import.invalidDataError')));
        continue;
      }
      const unit = this.extractMeasurementUnitFromString(quantity);

      const cable = this.cables.find((c: Cable) => c.id === partNumber);
      const product: QuoteProduct = {
        ...getDefaultQuoteProduct(false, count),
        partNumber: partNumber,
        quantity: this.convertQuantity(
          this.extractNumberFromString(quantity),
          unit
        ),
        price: this.extractNumberFromString(unitPrice),
      };
      this.products.push(
        cable ? updateQuoteProductFromCable(product, cable) : product
      );
      count++;
    }

    if (this.products.length === 0) {
      this.errors.push(Error(this.t('import.noValidDataError')));
    }
  }

  static import(
    data: string,
    t: TFunction,
    cables: Cable[],
    units: MeasurementUnits,
    quote: Quote | null
  ) {
    const report = new QuoteImportCsv(data, t, cables, units, quote);
    report.parse();
    return { products: report.products, errors: report.errors };
  }
}
